import { useState, useEffect } from 'react';
import { supabase } from '../supabase';

const GOLD = '#FFC220';

export default function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userCoupons, setUserCoupons] = useState([]);
  const [allCoupons, setAllCoupons] = useState([]);
  const [loadingCoupons, setLoadingCoupons] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    fetchUsers();
    fetchAllCoupons();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .order('created_at', { ascending: false });

    if (!error) setUsers(data);
    setLoading(false);
  };

  const fetchAllCoupons = async () => {
    const { data } = await supabase
      .from('coupons')
      .select('id, merchant_name, deal, expiry_date, active_fmp')
      .eq('active_fmp', true)
      .order('merchant_name', { ascending: true });

    if (data) setAllCoupons(data);
  };

  const fetchUserCoupons = async (userId) => {
    setLoadingCoupons(true);
    const { data } = await supabase
      .from('user_coupons')
      .select(`
        id,
        redeemed,
        redeemed_at,
        assigned_at,
        assignment_reason,
        coupons (
          id,
          merchant_name,
          deal,
          expiry_date
        )
      `)
      .eq('user_id', userId)
      .order('assigned_at', { ascending: false });

    if (data) setUserCoupons(data);
    setLoadingCoupons(false);
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    fetchUserCoupons(user.id);
  };

  const handleAssignAllCoupons = async () => {
    if (!selectedUser) return;
    if (!window.confirm(`Assign all active coupons to ${selectedUser.name}?`)) return;

    const { data: existingAssignments } = await supabase
      .from('user_coupons')
      .select('coupon_id')
      .eq('user_id', selectedUser.id);

    const existingIds = existingAssignments?.map((uc) => uc.coupon_id) || [];

    const newAssignments = allCoupons
      .filter((c) => !existingIds.includes(c.id))
      .map((c) => ({
        user_id: selectedUser.id,
        coupon_id: c.id,
        assignment_reason: 'manual',
        redeemed: false,
      }));

    if (newAssignments.length === 0) {
      alert('This user already has all active coupons assigned.');
      return;
    }

    await supabase.from('user_coupons').insert(newAssignments);
    setSuccessMsg(`${newAssignments.length} coupons assigned to ${selectedUser.name}!`);
    setTimeout(() => setSuccessMsg(''), 3000);
    fetchUserCoupons(selectedUser.id);
  };

  const handleRevokeCoupon = async (userCouponId) => {
    if (!window.confirm('Remove this coupon from the user?')) return;
    await supabase.from('user_coupons').delete().eq('id', userCouponId);
    fetchUserCoupons(selectedUser.id);
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm('Delete this user? All their coupon assignments will also be deleted.')) return;
    await supabase.from('user_coupons').delete().eq('user_id', id);
    await supabase.from('users').delete().eq('id', id);
    if (selectedUser?.id === id) setSelectedUser(null);
    fetchUsers();
  };

  const filtered = users.filter((u) =>
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase())
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  if (loading) return (
    <div style={styles.loading}><p style={{ color: GOLD }}>Loading...</p></div>
  );

  return (
    <div style={styles.wrap}>

      {/* TOPBAR */}
      <div style={styles.topbar}>
        <h1 style={styles.topbarTitle}>
          Users
          <span style={styles.topbarCount}>{users.length} registered</span>
        </h1>
      </div>

      <div style={styles.content}>

        {successMsg && (
          <div style={styles.successBanner}>✅ {successMsg}</div>
        )}

        <div style={styles.twoCol}>

          {/* USER LIST */}
          <div style={styles.leftCol}>
            <div style={styles.searchRow}>
              <input
                style={styles.searchInput}
                type="text"
                placeholder="Search users..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div style={styles.userList}>
              {filtered.length === 0 ? (
                <div style={styles.empty}>No users found.</div>
              ) : (
                filtered.map((user) => (
                  <div
                    key={user.id}
                    style={{
                      ...styles.userRow,
                      ...(selectedUser?.id === user.id ? styles.userRowActive : {}),
                    }}
                    onClick={() => handleSelectUser(user)}
                  >
                    <div style={styles.userAvatar}>
                      {(user.name || 'U')[0].toUpperCase()}
                    </div>
                    <div style={styles.userInfo}>
                        <div style={styles.userName}>{user.name || 'Unknown'}</div>
                        <div style={styles.userMemberId}>
                          ID: {user.member_id || '—'}
                        </div>
                        <div style={styles.userEmail}>{user.email}</div>
                        <div style={styles.userLastLogin}>
                          Last login: {formatDate(user.last_login)}
                        </div>
                      </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* USER DETAIL */}
          <div style={styles.rightCol}>
            {!selectedUser ? (
              <div style={styles.noSelection}>
                <p style={styles.noSelectionText}>Select a user to view details</p>
              </div>
            ) : (
              <div>
                {/* User header */}
                <div style={styles.userDetailHeader}>
                  <div style={styles.userDetailAvatar}>
                    {(selectedUser.name || 'U')[0].toUpperCase()}
                  </div>
                  <div style={styles.userDetailInfo}>
                    <div style={styles.userDetailName}>{selectedUser.name || 'Unknown'}</div>
                    <div style={styles.userDetailEmail}>{selectedUser.email}</div>
                    {selectedUser.card_number && (
                      <div style={styles.userDetailCard}>
                        Card: {selectedUser.card_number}
                      </div>
                    )}
                    {selectedUser.member_id && (
                      <div style={styles.userDetailCard}>
                        Member ID: {selectedUser.member_id}
                      </div>
                    )}
                  </div>
                  <button
                    style={styles.deleteUserBtn}
                    onClick={() => handleDeleteUser(selectedUser.id)}
                  >
                    Delete User
                  </button>
                </div>

                {/* Stats row */}
                <div style={styles.userStats}>
                  <div style={styles.userStat}>
                    <div style={styles.userStatNum}>
                      {userCoupons.filter((uc) => !uc.redeemed).length}
                    </div>
                    <div style={styles.userStatLabel}>Active Coupons</div>
                  </div>
                  <div style={styles.userStat}>
                    <div style={{ ...styles.userStatNum, color: GOLD }}>
                      {userCoupons.filter((uc) => uc.redeemed).length}
                    </div>
                    <div style={styles.userStatLabel}>Redeemed</div>
                  </div>
                  <div style={styles.userStat}>
                    <div style={styles.userStatNum}>{userCoupons.length}</div>
                    <div style={styles.userStatLabel}>Total Assigned</div>
                  </div>
                </div>

                {/* Coupon section header */}
                <div style={styles.couponSectionHeader}>
                  <h3 style={styles.couponSectionTitle}>Assigned Coupons</h3>
                  <button
                    style={styles.assignAllBtn}
                    onClick={handleAssignAllCoupons}
                  >
                    + Assign All Active Coupons
                  </button>
                </div>

                {/* Coupon list */}
                {loadingCoupons ? (
                  <p style={{ color: '#888', fontSize: '13px' }}>Loading coupons...</p>
                ) : userCoupons.length === 0 ? (
                  <div style={styles.empty}>No coupons assigned to this user.</div>
                ) : (
                  <div style={styles.couponList}>
                    {userCoupons.map((uc) => (
                      <div key={uc.id} style={{
                        ...styles.couponItem,
                        ...(uc.redeemed ? styles.couponItemRedeemed : {}),
                      }}>
                        <div style={styles.couponItemInfo}>
                          <div style={styles.couponItemMerchant}>
                            {uc.coupons?.merchant_name}
                          </div>
                          <div style={styles.couponItemDeal}>
                            {uc.coupons?.deal}
                          </div>
                          <div style={styles.couponItemMeta}>
                            Assigned {formatDate(uc.assigned_at)}
                            {uc.redeemed && ` · Redeemed ${formatDate(uc.redeemed_at)}`}
                          </div>
                        </div>
                        <div style={styles.couponItemRight}>
                          {uc.redeemed ? (
                            <span style={styles.redeemedBadge}>Redeemed</span>
                          ) : (
                            <button
                              style={styles.revokeBtn}
                              onClick={() => handleRevokeCoupon(uc.id)}
                            >
                              Revoke
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}

const styles = {
  wrap: { display: 'flex', flexDirection: 'column', minHeight: '100vh' },
  loading: { display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' },
  topbar: {
    background: '#fff',
    borderBottom: '1px solid #e0e0e0',
    padding: '16px 24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'sticky',
    top: 0,
    zIndex: 10,
  },
  topbarTitle: {
    fontSize: '18px',
    fontWeight: '500',
    color: '#111',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  topbarCount: {
    fontSize: '13px',
    fontWeight: '400',
    color: '#aaa',
  },
  content: { padding: '24px', flex: 1 },
  successBanner: {
    background: '#f0fff4',
    border: '1px solid #86efac',
    borderRadius: '10px',
    padding: '12px 16px',
    marginBottom: '20px',
    color: '#166534',
    fontSize: '14px',
  },
  twoCol: {
    display: 'grid',
    gridTemplateColumns: '320px 1fr',
    gap: '20px',
    alignItems: 'start',
  },
  leftCol: {
    background: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '12px',
    overflow: 'hidden',
  },
  searchRow: {
    padding: '12px',
    borderBottom: '1px solid #f0f0f0',
  },
  searchInput: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '13px',
    outline: 'none',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  },
  userList: {
    maxHeight: '600px',
    overflowY: 'auto',
  },
  userRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    borderBottom: '1px solid #f5f5f5',
    cursor: 'pointer',
  },
  userRowActive: {
    background: 'rgba(255,194,32,0.08)',
    borderLeft: `3px solid ${GOLD}`,
  },
  userAvatar: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    background: '#111',
    color: GOLD,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: '600',
    flexShrink: 0,
  },
  userInfo: { flex: 1, minWidth: 0 },
  userName: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#111',
    marginBottom: '2px',
  },
  userEmail: {
    fontSize: '12px',
    color: '#888',
    marginBottom: '2px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  userLastLogin: {
    fontSize: '11px',
    color: '#bbb',
  },
  rightCol: {
    background: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '12px',
    padding: '20px',
    minHeight: '400px',
  },
  noSelection: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '300px',
  },
  noSelectionText: {
    color: '#bbb',
    fontSize: '14px',
  },
  userDetailHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '14px',
    marginBottom: '20px',
    paddingBottom: '20px',
    borderBottom: '1px solid #f0f0f0',
  },
  userDetailAvatar: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    background: '#111',
    color: GOLD,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    fontWeight: '600',
    flexShrink: 0,
  },
  userDetailInfo: { flex: 1 },
  userDetailName: {
    fontSize: '16px',
    fontWeight: '500',
    color: '#111',
    marginBottom: '3px',
  },
  userDetailEmail: {
    fontSize: '13px',
    color: '#888',
    marginBottom: '3px',
  },
  userDetailCard: {
    fontSize: '12px',
    color: '#aaa',
    fontFamily: 'monospace',
  },
  deleteUserBtn: {
    background: 'none',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    padding: '6px 12px',
    fontSize: '12px',
    color: '#ff6b6b',
    cursor: 'pointer',
    flexShrink: 0,
  },
  userStats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '12px',
    marginBottom: '20px',
  },
  userStat: {
    background: '#f9f9f9',
    borderRadius: '10px',
    padding: '12px',
    textAlign: 'center',
  },
  userStatNum: {
    fontSize: '24px',
    fontWeight: '500',
    color: '#111',
    lineHeight: 1,
    marginBottom: '4px',
  },
  userStatLabel: {
    fontSize: '11px',
    color: '#888',
  },
  couponSectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  couponSectionTitle: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#111',
    margin: 0,
  },
  assignAllBtn: {
    background: GOLD,
    color: '#111',
    border: 'none',
    borderRadius: '8px',
    padding: '6px 12px',
    fontSize: '12px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  couponList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  couponItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: '#f9f9f9',
    border: '1px solid #e0e0e0',
    borderRadius: '10px',
    padding: '12px 14px',
  },
  couponItemRedeemed: {
    opacity: 0.5,
  },
  couponItemInfo: { flex: 1 },
  couponItemMerchant: {
    fontSize: '13px',
    fontWeight: '500',
    color: '#111',
    marginBottom: '2px',
  },
  couponItemDeal: {
    fontSize: '12px',
    color: '#555',
    marginBottom: '4px',
  },
  couponItemMeta: {
    fontSize: '11px',
    color: '#aaa',
  },
  couponItemRight: { flexShrink: 0, marginLeft: '12px' },
  redeemedBadge: {
    background: '#f0f0f0',
    color: '#888',
    fontSize: '11px',
    padding: '3px 8px',
    borderRadius: '20px',
  },
  revokeBtn: {
    background: 'none',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    padding: '4px 10px',
    fontSize: '12px',
    color: '#ff6b6b',
    cursor: 'pointer',
  },
  empty: {
    padding: '24px',
    textAlign: 'center',
    color: '#888',
    fontSize: '13px',
  },
  userMemberId: {
    fontSize: '12px',
    color: GOLD,
    fontFamily: 'monospace',
    marginBottom: '2px',
    fontWeight: '500',
  },
};